import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GROUPS = [
  { key: 'students', label: 'Students', tab: 'students' },
  { key: 'recruiters', label: 'Recruiters', tab: 'recruiters' },
  { key: 'colleges', label: 'Colleges & Programs', tab: 'colleges' },
  { key: 'commissions', label: 'Commissions', tab: 'colleges' },
  { key: 'leads', label: 'Leads', tab: 'leads' },
  { key: 'activities', label: 'Sales Activity', tab: 'lead_activity' },
  { key: 'messages', label: 'Chats & Messages', tab: 'messages' },
  { key: 'notes', label: 'Student Notes', tab: 'students' },
  { key: 'documents', label: 'Document Archive', tab: 'documents' },
];

function itemTitle(group, item) {
  switch (group) {
    case 'students': return `${item.student_name || item.student_id} · ${item.email || ''}`;
    case 'recruiters': return `${item.company_name || item.recruiter_id} · ${item.contact_email || item.email || ''}`;
    case 'colleges': return `${item.name}${item.matched_programs?.length ? ` — ${item.matched_programs.map((p) => `${p.program_name} (${p.currency || ''} ${Number(p.tuition_fee || 0).toLocaleString()})`).join(', ')}` : ` · ${item.programs_count} programs`}`;
    case 'commissions': return `${item.recruiter_name || item.recruiter_id} · ${item.college_name || ''} · ${item.program_name} — ${item.commission_type === 'percentage' ? `${item.commission_value}%` : item.commission_value}`;
    case 'leads': return `${item.name || item.full_name || 'Lead'} · ${item.email || item.phone || ''} (${item.status || ''})`;
    case 'activities': return `${item.actor_name || ''} · ${item.lead_name || ''} — ${item.type || ''}${item.note ? `: ${item.note.slice(0, 60)}` : ''}`;
    case 'messages': return `${item.sender_name || ''}: ${(item.body || item.attachment_name || '').slice(0, 70)}`;
    case 'notes': return `${item.author_name || ''} → ${item.student_id}: ${(item.body || '').slice(0, 60)}`;
    case 'documents': return `${item.owner_name || item.owner_id} — ${(item.doc_key || '').replace(/_/g, ' ')}`;
    default: return '';
  }
}

/** Global search box for admin/staff headers — searches every module and
 *  jumps to the matching dashboard tab on click. */
export default function MasterSearch({ onNavigate }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/search?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' });
        if (res.ok) {
          setResults((await res.json()).results || {});
          setOpen(true);
        }
      } catch { /* offline */ } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  const totalHits = results ? Object.values(results).reduce((n, arr) => n + arr.length, 0) : 0;

  return (
    <div className="relative w-full max-w-md" ref={boxRef} data-testid="master-search">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-tge-navy/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Search everything — students, recruiters, colleges, leads, chats…"
          className="w-full h-10 pl-9 pr-9 rounded-full border border-tge-navy/15 bg-white/90 text-sm focus:outline-none focus:border-tge-navy/40 transition"
          data-testid="master-search-input"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-tge-navy/40" />}
      </div>

      {open && results && (
        <div
          className="absolute top-12 left-0 right-0 bg-white border border-tge-navy/10 rounded-2xl shadow-xl max-h-[70vh] overflow-y-auto z-50"
          data-testid="master-search-results"
        >
          {totalHits === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-tge-muted">No results for “{q}”.</div>
          ) : (
            GROUPS.filter((g) => results[g.key]?.length).map((g) => (
              <div key={g.key} className="py-1">
                <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider font-bold text-tge-gold flex items-center justify-between">
                  <span>{g.label}</span>
                  <span className="text-tge-navy/30 font-medium">{results[g.key].length}</span>
                </div>
                {results[g.key].map((item, i) => (
                  <button
                    key={i}
                    onClick={() => { setOpen(false); onNavigate(g.tab, item); }}
                    className="w-full text-left px-4 py-2 text-xs text-tge-navy hover:bg-tge-cream-soft transition truncate block"
                    data-testid={`search-result-${g.key}-${i}`}
                  >
                    {itemTitle(g.key, item)}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
